import { Box, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { TOOLBAR_COLORS } from "components/toolbars/constants";

interface FilterTokenProps {
    label: string;
    count: number;
    showCount?: boolean;
    isEnabled: boolean;
    onToggle?: () => void;
    onClear: () => void;
    onActivate?: () => void;
}

function FilterTokenToggle({ enabled, onToggle }: { enabled: boolean; onToggle?: () => void }) {
    return (
        <Box
            component="button"
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onToggle?.();
            }}
            disabled={!onToggle}
            sx={{
                width: 36,
                height: 20,
                borderRadius: "16px",
                backgroundColor: enabled ? TOOLBAR_COLORS.TOKEN_TOGGLE_ON : TOOLBAR_COLORS.TOKEN_TOGGLE_OFF,
                border: "none",
                padding: "2px",
                display: "flex",
                alignItems: "center",
                cursor: onToggle ? "pointer" : "default",
                flexShrink: 0,
                transition: "background-color 150ms",
                position: "relative",
                "&:disabled": { opacity: 0.5 },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "2px",
                    left: enabled ? "18px" : "2px",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: TOOLBAR_COLORS.WHITE,
                    boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                    transition: "left 150ms",
                }}
            />
        </Box>
    );
}

export function FilterToken({
    label,
    count,
    showCount = false,
    isEnabled,
    onToggle,
    onClear,
    onActivate,
}: FilterTokenProps) {
    const { t } = useTranslation();
    return (
        <Box
            onClick={onActivate}
            sx={{
                display: "flex",
                height: 36,
                width: "100%",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                borderRadius: "4px",
                backgroundColor: TOOLBAR_COLORS.TOKEN_ROW_BG,
                px: "6px",
                py: "4px",
                cursor: onActivate ? "pointer" : "default",
                transition: "background-color 150ms",
                "&:hover": onActivate ? { backgroundColor: TOOLBAR_COLORS.TOKEN_ROW_BG_HOVER } : undefined,
            }}
        >
            <Box sx={{ display: "flex", minWidth: 0, alignItems: "center", gap: "8px", px: "6px", py: "2px" }}>
                <FilterTokenToggle enabled={isEnabled} onToggle={onToggle} />
                <Typography
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: "20px",
                        letterSpacing: "0.15px",
                        color: TOOLBAR_COLORS.TOKEN_TEXT,
                    }}
                >
                    {showCount ? `${label} (${count})` : label}
                </Typography>
            </Box>
            <Box
                component="button"
                type="button"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onClear();
                }}
                aria-label={t("viewModifications.removeLabel", { label })}
                sx={{
                    display: "flex",
                    flexShrink: 0,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                    padding: "4px",
                    color: TOOLBAR_COLORS.TOKEN_TEXT,
                    border: "none",
                    backgroundColor: "transparent",
                    cursor: "pointer",
                    transition: "background-color 150ms",
                    "&:hover": { backgroundColor: TOOLBAR_COLORS.TOKEN_CLEAR_HOVER },
                }}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </Box>
        </Box>
    );
}
